/**
 * GenderBreakdownSection.tsx — Looker Studio style
 *
 * Layout:
 *   Row 1: Male KPI card | Leads by Gender pie | Female KPI card
 *   Row 2: Spend by Gender (daily) | Clicks by Gender (daily)
 *
 * Unknown gender filtered out entirely.
 */

import React, { useMemo } from "react";
import {
  PieChart, Pie, Cell,
  LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { branding } from "../../../../config/branding";
import type { GenderSummaryRow, GenderDailyRow } from "../genderBreakdown";
import { formatCurrency, formatNumber } from "../formatters";

// ─────────────────────────────────────────────
// DESIGN TOKENS (consistent with other breakdown sections)
// ─────────────────────────────────────────────
const C = branding.colors;
const COLOR_MALE   = C.gender?.male   ?? "#3b82f6";  // blue-500  — Male
const COLOR_FEMALE = C.gender?.female ?? "#ec4899";  // pink-500  — Female

const TICK_STYLE = { fontSize: 10, fill: "currentColor", opacity: 0.5 } as const;
const AXIS_PROPS = { axisLine: false, tickLine: false, tick: TICK_STYLE } as const;
const GRID_PROPS = { strokeDasharray: "3 3", vertical: false, stroke: "currentColor", strokeOpacity: 0.08 } as const;
const CHART_H = 220;

// ─────────────────────────────────────────────
// SHARED CARD WRAPPER
// ─────────────────────────────────────────────
function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <h3 className="text-sm font-semibold text-foreground mb-4">{title}</h3>
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────
// LEADS BY GENDER PIE (center column)
// ─────────────────────────────────────────────
function PieTooltipContent({ active, payload }: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; payload: { fill: string } }>;
}) {
  if (!active || !payload?.length) return null;
  const item = payload[0];
  return (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 shadow-xl text-xs">
      <div className="flex items-center gap-2">
        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: item.payload.fill }} />
        <span className="text-muted-foreground capitalize">{item.name}</span>
        <span className="font-semibold text-foreground ml-1">{formatNumber(item.value)}</span>
      </div>
    </div>
  );
}

function LeadsPie({ summary }: { summary: GenderSummaryRow[] }) {
  const total = summary.reduce((s, r) => s + r.optins, 0);
  const pieData = summary.map(r => ({
    name: r.label,
    value: r.optins,
    fill: r.gender === "male" ? COLOR_MALE : COLOR_FEMALE,
  }));

  return (
    <ChartCard title="Opt-ins by Gender">
      <div className="relative" style={{ height: CHART_H }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              innerRadius="48%"
              outerRadius="70%"
              dataKey="value"
              paddingAngle={3}
              isAnimationActive={false}
            >
              {pieData.map((entry, i) => (
                <Cell key={i} fill={entry.fill} />
              ))}
            </Pie>
            <Tooltip content={props => <PieTooltipContent {...props} />} />
          </PieChart>
        </ResponsiveContainer>
        {/* Center label */}
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span className="text-2xl font-bold text-foreground leading-none">{formatNumber(total)}</span>
          <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest mt-1">total leads</span>
        </div>
      </div>

      {/* Legend with % */}
      <div className="flex justify-center gap-8 mt-3">
        {summary.map(r => (
          <div key={r.gender} className="flex flex-col items-center gap-0.5">
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full" style={{ background: r.gender === "male" ? COLOR_MALE : COLOR_FEMALE }} />
              <span className="text-[10px] font-medium text-muted-foreground">{r.label}</span>
            </div>
            <span className="text-base font-bold text-foreground">
              {total > 0 ? `${((r.optins / total) * 100).toFixed(1)}%` : "0%"}
            </span>
            <span className="text-[10px] text-muted-foreground">{formatNumber(r.optins)} leads</span>
          </div>
        ))}
      </div>
    </ChartCard>
  );
}

// ─────────────────────────────────────────────
// GENDER KPI CARD
// ─────────────────────────────────────────────
function GenderKpiCard({ row }: { row: GenderSummaryRow }) {
  const color = row.gender === "male" ? COLOR_MALE : COLOR_FEMALE;

  return (
    <div className="rounded-xl border border-border bg-card p-5 flex flex-col">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: color }} />
        <span className="text-xs font-bold uppercase tracking-widest" style={{ color }}>{row.label}</span>
      </div>

      <div className="mb-4">
        <span className="text-3xl font-bold text-foreground leading-none">{formatNumber(row.optins)}</span>
        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest mt-0.5">opt-ins</p>
      </div>

      <div className="space-y-2 border-t border-border pt-3 mt-auto">
        {[
          { label: "Spend",       value: formatCurrency(row.spend) },
          { label: "Cost/Opt-in", value: row.optins > 0 ? formatCurrency(row.costPerOptin) : "N/A" },
        ].map(r => (
          <div key={r.label} className="flex items-center justify-between">
            <span className="text-[10px] text-muted-foreground uppercase tracking-widest">{r.label}</span>
            <span className="text-xs font-semibold text-foreground">{r.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// DAILY LINE TOOLTIP
// ─────────────────────────────────────────────
function LineTooltip({ active, payload, label, fmt }: {
  active?: boolean;
  payload?: Array<{ dataKey: string; value: number; color: string; name: string }>;
  label?: string;
  fmt: (v: number) => string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 shadow-xl text-xs">
      <p className="text-[10px] font-semibold text-muted-foreground mb-1.5">{label}</p>
      {payload.map(p => (
        <div key={p.dataKey} className="flex items-center gap-2 mb-0.5 last:mb-0">
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: p.color }} />
          <span className="text-muted-foreground">{p.name}:</span>
          <span className="font-semibold text-foreground">{fmt(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────
// DAILY LINE CHART (Male / Female only)
// ─────────────────────────────────────────────
function GenderDailyLine({
  title, data, maleKey, femaleKey, fmt, axisFmt,
}: {
  title: string;
  data: GenderDailyRow[];
  maleKey: keyof GenderDailyRow;
  femaleKey: keyof GenderDailyRow;
  fmt: (v: number) => string;
  axisFmt: (v: number) => string;
}) {
  const interval = Math.max(0, Math.ceil(data.length / 8) - 1);
  const lines = [
    { key: maleKey as string,   label: "Male",   color: COLOR_MALE },
    { key: femaleKey as string, label: "Female", color: COLOR_FEMALE },
  ];

  return (
    <ChartCard title={title}>
      {/* Legend */}
      <div className="flex items-center gap-5 mb-3">
        {lines.map(l => (
          <div key={l.key} className="flex items-center gap-1.5">
            <span className="inline-block w-5 h-[2px] rounded-full" style={{ background: l.color }} />
            <span className="text-[10px] text-muted-foreground">{l.label}</span>
          </div>
        ))}
      </div>

      <div style={{ height: CHART_H }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid {...GRID_PROPS} />
            <XAxis dataKey="date" {...AXIS_PROPS} dy={6} interval={interval} />
            <YAxis {...AXIS_PROPS} tickFormatter={axisFmt} width={52} />
            <Tooltip
              content={props => (
                <LineTooltip
                  active={props.active}
                  payload={props.payload?.map(p => ({
                    dataKey: String(p.dataKey),
                    value: Number(p.value),
                    color: p.color ?? "",
                    name: p.name ?? "",
                  }))}
                  label={String(props.label ?? "")}
                  fmt={fmt}
                />
              )}
              cursor={{ stroke: "currentColor", strokeOpacity: 0.1 }}
            />
            {lines.map(l => (
              <Line
                key={l.key}
                type="monotone"
                dataKey={l.key}
                name={l.label}
                stroke={l.color}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, strokeWidth: 0 }}
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </ChartCard>
  );
}

// ─────────────────────────────────────────────
// MAIN EXPORT
// ─────────────────────────────────────────────
interface GenderBreakdownSectionProps {
  summary: GenderSummaryRow[];
  dailyData: GenderDailyRow[];
}

export function GenderBreakdownSection({ summary, dailyData }: GenderBreakdownSectionProps) {
  const filtered = useMemo(() => summary.filter(r => r.gender !== "unknown"), [summary]);
  const male   = filtered.find(r => r.gender === "male");
  const female = filtered.find(r => r.gender === "female");

  const spndFmt = (v: number) => v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${v.toFixed(0)}`;
  const numFmt  = (v: number) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(Math.round(v));

  return (
    <div className="space-y-4">
      {/* Section header */}
      <div className="flex items-center gap-3 pt-2">
        <div className="h-px flex-1 bg-border" />
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-widest px-2">
          Gender Breakdown
        </span>
        <div className="h-px flex-1 bg-border" />
      </div>

      {/* Row 1: Leads Pie | Male KPI | Female KPI */}
      <div className="grid grid-cols-3 gap-4">
        <LeadsPie summary={filtered} />
        {male   && <GenderKpiCard row={male} />}
        {female && <GenderKpiCard row={female} />}
      </div>

      {/* Row 2: Daily spend + clicks */}
      <div className="grid grid-cols-2 gap-4">
        <GenderDailyLine
          title="Spend by Gender (Daily)"
          data={dailyData}
          maleKey="male_spend"
          femaleKey="female_spend"
          fmt={formatCurrency}
          axisFmt={spndFmt}
        />
        <GenderDailyLine
          title="Opt-ins by Gender (Daily)"
          data={dailyData}
          maleKey="male_optins"
          femaleKey="female_optins"
          fmt={formatNumber}
          axisFmt={numFmt}
        />
      </div>
    </div>
  );
}
